import { Injectable, OnDestroy } from '@angular/core';
import documentListingQuery from '@graphql/fund-document/fund-documents-all-products.graphql';
import { ADVISOR, Z_SHARECLASS } from '@literature/const/literature-data-const';
import { fundNameGenerator, nameGenerator } from '@literature/literature-utils';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import { LiteratureFundDataService } from '@literature/services/literature-fund-data.service';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { Product, ShareClass } from '@models';
import { FundFilterService } from '@products/fund-listing/services/fund-filter.service';
import { ProductTypeParameter } from '@products/services/graphql-fund-data.service';
import { DebugService } from '@services/debug.service';
import { IUserProfile } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { SegmentService } from '@services/segment.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  ConfigurationId,
  CustomLiteratureDocumentListingData,
  DctermType,
  DocumentDctermType,
  DocumentMatchCondition,
  FundId,
  KeyIdentifiers,
  LiteratureDocument,
  LiteratureDocumentListingData,
  LiteratureListingConfiguration,
} from '@types';
import { Logger } from '@utils/logger';
import { stringCompare } from '@utils/sort-utils';
import cloneDeep from 'lodash/cloneDeep';
import { forkJoin, Observable, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { LiteratureListingBaseService } from './literature-listing-base.service';
import { PagedRecords } from '@frk/eds-components';

const logger = Logger.getLogger('LiteratureDocumentListingService');

@Injectable({
  providedIn: 'root',
})
export class LiteratureDocumentListingService
  extends LiteratureListingBaseService
  implements OnDestroy {
  listingData: LiteratureDocumentListingData[];
  customListingData: CustomLiteratureDocumentListingData[] = [];
  dateFormat: string;
  dataInitialised = false;
  dataState: string;
  pagedData: PagedRecords<LiteratureDocumentListingData> = new PagedRecords<LiteratureDocumentListingData>(
    100
  ); // TODO: use const/config for pageSize
  totalNumberOfData = new Subject();
  literaturePageConfig: LiteratureListingConfiguration;
  constructor(
    protected literatureDataService: LiteratureDataService,
    private fundDataService: LiteratureFundDataService,
    protected segmentService: SegmentService,
    private filterService: FundFilterService,
    protected translateService: TranslateService,
    protected cartService: CartHandlerService,
    protected profileService: ProfileService,
    protected literatureConfigService: LiteratureGlobalConfigService,
    protected debugService: DebugService
  ) {
    super(
      literatureDataService,
      literatureConfigService,
      cartService,
      segmentService,
      profileService,
      translateService
    );
    this.filterService.getData().subscribe(([dataType, data]) => {
      if ('filteredList' === dataType) {
        this.dataState = dataType;
        this.dataInitialised = true;
        this.state.data = data;
        this.documentListingData$.next(this.state);
        this.pagedData.records = data;
      }
    });
    this.debugService
      .getExpandAllComponents$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expandAll: boolean): void => {
        if (expandAll) {
          this.pagedData.expandAll();
        }
      });
  }

  dataMap: Map<FundId, Product> = new Map();
  fundUmbrellaMap: Map<string, [any]> = new Map<string, [any]>();

  // both the object's key and size is dynamic and based on number of shareclasses combination document has.
  singleShareClassData: object = {};
  multiShareClassData: object = {};

  populate(param: Map<string, string>, isCustom: boolean): void {
    this.literaturePageConfig = this.literatureConfigService.getLiteratureListingConfig();
    // Audience new flow
    if (this.literatureConfigService.checkAudienceFromOverrideList()) {
      param.set(
        'audience',
        this.literaturePageConfig?.segmentOverrideList?.toString()
      );
    }
    if (this.isInternational) {
      forkJoin([
        this.fundDataService.register(
          documentListingQuery,
          {
            configurationName: ConfigurationId.MF,
            productType: ProductTypeParameter.MUTUAL_FUNDS,
          },
          this.literatureConfigService.literatureConfig?.activeList,
          this.literatureConfigService.literatureConfig?.fundOverrideFlag
        ),
        this.literatureDataService.loadData(param),
      ]).subscribe(
        (response) => {
          if (isCustom) {
            this.customMerge(response);
          } else {
            this.mergeData(response);
          }
        },
        (err) => {
          if (err?.status === 404) {
            logger.warn(
              '[populate] ' +
                err?.messaage +
                ' code: ' +
                err?.error?.error?.errorList[0]?.code
            );
          }
          this.state.isError = true;
          this.state.data = [];
          this.documentListingData$.next(this.state);
        }
      );
    } else {
      this.literatureDataService
        .getProfileData$()
        ?.pipe(
          takeUntil(this.unsubscribe$),
          switchMap((profileData: IUserProfile) => {
            this.literatureDataService.setProfileData(profileData);
            return forkJoin([
              this.fundDataService.register(
                documentListingQuery,
                {
                  configurationName: ConfigurationId.MF,
                  productType: ProductTypeParameter.MUTUAL_FUNDS,
                },
                this.literatureConfigService.literatureConfig?.activeList,
                this.literatureConfigService.literatureConfig?.fundOverrideFlag
              ),
              this.literatureDataService.loadData(param, true),
            ]);
          })
        )
        .subscribe(
          (response) => {
            if (isCustom) {
              this.customMerge(response);
            } else {
              this.mergeData(response);
            }
          },
          (err) => {
            if (err?.status === 404) {
              logger.warn(
                '[populate] ' +
                  err?.messaage +
                  ' code: ' +
                  err?.error?.error?.errorList[0]?.code
              );
            }
            this.state.isError = true;
            this.state.data = [];
            this.documentListingData$.next(this.state);
          }
        );
    }
  }

  getColumnToDisplay(): string[] {
    return this.literaturePageConfig?.literatureListGridColumn;
  }
  getLoggedInColumnToDisplay(): string[] {
    return this.literaturePageConfig?.documentTypeDisplayForLoggedIn;
  }
  /**
   * Prepares data for literature listing page from PPSS data and literature data
   * @param gridData LiteratureDocumentListingData
   * @returns LiteratureDocumentListingData
   */
  private mergeData(gridData): LiteratureDocumentListingData[] {
    this.loggedInColumnToDisplay = this.getLoggedInColumnToDisplay();
    logger.debug('merge data: ', gridData);
    this.listingData = [];
    const [fundListing, litData] = gridData;
    this.dateFormat = this.literatureConfigService.dateFormat;

    this.productDataMergeInitializer(fundListing._products);
    const colToDisplay = this.literaturePageConfig?.documentTypeDisplay;
    const filters = [
      ...this.literaturePageConfig?.primaryFilters,
      ...this.literaturePageConfig?.additionalFilters,
    ];
    for (const literatureData of litData.document) {
      let litSet: Set<string>;
      const data = this.literatureDataService.literatureMapper(literatureData);

      // WDE-441 flag usePreviewLink informs components to redirect user to preview page instead download.
      data.usePreviewLink = this.literatureConfigService?.literatureConfig?.documentsWithPreview?.includes(
        data.dctermsType
      );

      if (
        !this.checkAudienceAndDocumentTypeConfiguration(
          data,
          filters,
          colToDisplay
        )
      ) {
        continue;
      }
      const frkOneTISNumber = literatureData.frkOneTISNumber
        ? literatureData.frkOneTISNumber.split(',')
        : [];
      if (!this.isInternational) {
        // when there is no association with funds need to show that documents also
        if (frkOneTISNumber.length === 0 && !data.frkFundGroupCode) {
          this.loadDocumentList(data, {}, this.listingData);
        }
        litSet = new Set<string>();
      }
      let matchFound = false;
      const matchOn = this.literatureConfigService
        .getDocumentMappingConfiguration()
        .get(data.dctermsType);
      switch (matchOn) {
        case DocumentMatchCondition.UNASSOCIATED:
          data.identifiers = this.initializeIdentifier();
          this.loadDocumentList(data, {}, this.listingData);
          break;
        // tslint:disable-next-line: no-switch-case-fall-through
        case DocumentMatchCondition.FUND_ID:
          matchFound = this.matchOnFundId(frkOneTISNumber, data, litSet);
          if (matchFound) {
            break;
          }
        // tslint:disable-next-line: no-switch-case-fall-through
        case DocumentMatchCondition.FUND_UMBRELLA:
          matchFound = this.matchOnFundUmbrella(data);
          if (matchFound) {
            break;
          }
        // tslint:disable-next-line: no-switch-case-fall-through
        default:
          if (!this.matchOnFundId(frkOneTISNumber, data, litSet)) {
            this.matchOnFundUmbrella(data);
          }
      }
    }

    this.listingData = this.sortData(this.listingData);
    this.state.data = this.listingData;
    this.filterService.init(this.listingData);
    this.totalNumberOfData.next(this.listingData.length);
    return this.listingData;
  }
  private matchOnFundId(
    frkOneTISNumber: string[],
    data: LiteratureDocument,
    litSet: Set<string>
  ): boolean {
    data.multipleProductsForSearch = {
      fundNames: [],
    };
    // lit api records based on onetis number which can be multiple with one document
    if (frkOneTISNumber.length > 0) {
      for (const element of frkOneTISNumber) {
        // if site is US then incase of multiple onetis number only add document for oneTis number
        // To avoid duplicate records

        if (!this.isInternational) {
          if (litSet.has(data.docId)) {
            break;
          }
          litSet.add(data.docId);
          // to store multiple products data associated to single literature document for 'SEARCH BY KEYWORD' filter
          data.multipleProductsForSearch.fundIds = cloneDeep(frkOneTISNumber);
          frkOneTISNumber.forEach((el) => {
            if (this.dataMap.get(el?.trim() as FundId)?.shareClasses) {
              data.multipleProductsForSearch.fundNames.push(
                this.dataMap.get(el?.trim() as FundId)?.fundName
              );
              data.multipleProductsForSearch.identifiers = this.getKeyIdentifiers(
                this.dataMap.get(el?.trim() as FundId)?.shareClasses,
                data.multipleProductsForSearch.identifiers
                  ? data.multipleProductsForSearch.identifiers
                  : this.initializeIdentifier()
              );
            }
          });
        }
        const productData: Product = this.dataMap.get(
          element?.trim() as FundId
        );
        if (
          this.dataMap.has(element?.trim() as FundId) &&
          productData.shareClasses?.length > 0
        ) {
          this.matchOnShareClass(data, productData);
        }
      }
      return true;
    }
    return false;
  }
  private matchOnFundUmbrella(data: LiteratureDocument): boolean {
    if (data.frkFundGroupCode) {
      const fundUmbrella = this.fundUmbrellaMap.get(
        data.frkFundGroupCode.trim()
      );
      if (fundUmbrella) {
        fundUmbrella.forEach((productData) => {
          if (productData.shareClasses?.length > 0) {
            this.matchOnShareClass(data, productData);
          }
        });
        return true;
      }
    }
    return false;
  }
  matchOnShareClass(data: LiteratureDocument, productData: any) {
    const matchOn = this.literatureConfigService
      .getDocumentMappingConfiguration()
      .get(data.dctermsType);
    if (
      (!matchOn && data.frkShareClassCode?.length > 1) ||
      (DocumentMatchCondition.FUND_SHARE_CLASS === matchOn &&
        data.frkShareClassCode?.length > 1)
    ) {
      for (const shareClassCode of data.frkShareClassCode) {
        const clonedLitData = cloneDeep(data);
        clonedLitData.frkShareClassCode = [shareClassCode];
        this.loadShareClassData(
          clonedLitData,
          productData,
          this.listingData,
          !(matchOn === DocumentMatchCondition.FUND_ID)
        );
      }
    } else {
      this.loadShareClassData(
        cloneDeep(data),
        productData,
        this.listingData,
        !(matchOn === DocumentMatchCondition.FUND_ID)
      );
    }
  }
  productDataMergeInitializer(ppssListing) {
    for (const productData of ppssListing) {
      // to map product data at fund ID level
      this.dataMap.set(productData.fundId, productData);
      const fundUmbrella = productData.fundUmbrella;
      // to map product data at fundUmberella level
      if (fundUmbrella) {
        if (this.fundUmbrellaMap.has(fundUmbrella)) {
          const ppssArray = this.fundUmbrellaMap.get(fundUmbrella);
          ppssArray.push(productData);
          this.fundUmbrellaMap.set(fundUmbrella, ppssArray);
        } else {
          this.fundUmbrellaMap.set(fundUmbrella, [productData]);
        }
      }
    }
  }

  customMerge(data): CustomLiteratureDocumentListingData[] {
    this.loggedInColumnToDisplay = this.getLoggedInColumnToDisplay();
    logger.debug('custom merge data: ', data);
    this.listingData = [];
    const [fundListing, litData] = data;
    this.dateFormat = this.literatureConfigService.dateFormat;
    const litDataMap: Map<
      FundId,
      Map<DctermType, LiteratureDocument[]>
    > = new Map();
    const columnToDisplay = this.literaturePageConfig?.documentTypeDisplay;

    this.productDataMergeInitializer(fundListing._products);

    for (const literatureData of litData.document) {
      const frkOneTISNumber = literatureData.frkOneTISNumber
        ? literatureData.frkOneTISNumber
            .split(',')
            .map((oneTisNumber) => oneTisNumber.trim())
        : [];
      data = this.literatureDataService.literatureMapper(literatureData);
      this.literatureDataService.mapLiteratureUrl(data);
      // to map all the data with same onetis Number
      if (frkOneTISNumber.length > 0) {
        frkOneTISNumber.forEach((element) => {
          if (columnToDisplay.indexOf(data.dctermsType) !== -1) {
            if (litDataMap.has(element)) {
              const mapData: Map<
                DctermType,
                LiteratureDocument[]
              > = litDataMap.get(element);
              if (mapData.has(data.dctermsType)) {
                const tempData = mapData.get(data.dctermsType);
                tempData.push(data);
                mapData.set(data.dctermsType, tempData);
              } else if (
                data.dctermsType !== DocumentDctermType.SHAREHOLDER_REPORT
              ) {
                mapData.set(data.dctermsType as DctermType, [data]);
              }
              litDataMap.set(element, mapData);
            } else {
              const litDataMapWithDctermType: Map<
                DctermType,
                LiteratureDocument[]
              > = new Map();
              if (data.dctermsType !== DocumentDctermType.SHAREHOLDER_REPORT) {
                litDataMapWithDctermType.set(data.dctermsType as DctermType, [
                  data,
                ]);
              }
              litDataMap.set(element, litDataMapWithDctermType);
            }
          }
        });
      }
    }
    litDataMap.forEach((value, key) => {
      const productData: Product = this.dataMap.get(key);
      if (
        this.dataMap.has(key) &&
        productData.shareClasses?.length > 0
        // && this.checkAudienceAndDocumentTypeConfiguration(data)
      ) {
        this.customLoadShareClassData(litDataMap.get(key), productData);
      }
    });
    this.customListingData = this.sortOnFundName(this.customListingData);
    this.state.data = this.customListingData;
    this.filterService.init(this.state.data);
    this.totalNumberOfData.next(this.customListingData.length);
    return this.customListingData;
  }

  sortOnFundName(data: any): any {
    const nameCompare = stringCompare;
    return data.sort((prevData, nextData) => {
      return nameCompare(
        prevData.product.fundName,
        nextData.product.fundName,
        null,
        null,
        false
      );
    });
  }

  getTotalNumberofData() {
    return this.totalNumberOfData;
  }

  sortData(
    data: LiteratureDocumentListingData[]
  ): LiteratureDocumentListingData[] {
    const nameCompare = stringCompare;
    return data.sort((a, b) => {
      return nameCompare(
        this.isInternational
          ? nameGenerator(
              a,
              this.translateService,
              this.literatureConfigService.isCanada
            )
          : fundNameGenerator(a, this.translateService),
        this.isInternational
          ? nameGenerator(
              b,
              this.translateService,
              this.literatureConfigService.isCanada
            )
          : fundNameGenerator(b, this.translateService),
        null,
        null,
        false
      );
    });
  }

  checkAudienceAndDocumentTypeConfiguration(
    data: LiteratureDocument,
    filters: string[],
    colToDisplay: string[]
  ) {
    const isLanguageFilterConfigured = filters.indexOf('LANGUAGE') > -1;
    const languageFilterArray: string[] = this.literaturePageConfig
      ?.languageFilterArray;
    let LanguageMatch = false;
    if (isLanguageFilterConfigured) {
      if (languageFilterArray?.length > 0) {
        for (const language of languageFilterArray) {
          if (data.dctermsLanguage?.indexOf(language) !== -1) {
            LanguageMatch = true;
            data.languageFilterValues.push(language);
            break;
          }
        }
      } else {
        data.languageFilterValues = [...data.dctermsLanguage];
      }
    }
    // Audience new flow logic to check map
    const checkDocsWithoutAudience =
      colToDisplay?.indexOf(data.dctermsType) !== -1 &&
      (isLanguageFilterConfigured && languageFilterArray?.length > 0
        ? LanguageMatch
        : true);
    // markOld:  Audience existing logic to check map
    const checkAudienceForDocs =
      this.checkAudience(
        data,
        this.literaturePageConfig?.segmentOverrideList
      ) && checkDocsWithoutAudience;

    // switch logic based on flags at channel level
    const checkMapping = this.literatureConfigService.checkAudienceFromOverrideList()
      ? checkDocsWithoutAudience
      : checkAudienceForDocs;

    if (checkMapping) {
      return true;
    }
    return false;
  }

  updateNumberOfRows(numberOfRows: number) {
    this.pagedData.pageSize = numberOfRows;
  }

  getPageSize(): number {
    return this.pagedData.pageSize;
  }

  goToPage(n: number): void {
    if (this.pagedData) {
      this.pagedData.pageNumber = n;
    }
  }

  getNumberOfPages$(): Observable<number> {
    return this.pagedData.numberOfPages$;
  }

  getNumberOfPages(): number {
    return this.pagedData.numberOfPages;
  }

  getPageNumber$(): Observable<number> {
    return this.pagedData.pageNumber$;
  }

  getCurrentPage$(): Observable<LiteratureDocumentListingData[]> {
    return this.pagedData.currentPage$;
  }

  loadShareClassData(
    litData: LiteratureDocument,
    ppssData: any,
    listingData: LiteratureDocumentListingData[],
    showShareClass: boolean
  ): void {
    this.checkShareClassAvailablity(litData, ppssData, showShareClass);
    if (litData.allShareClass.length > 0) {
      litData.identifiers = this.getKeyIdentifiers(
        litData.allShareClass,
        this.initializeIdentifier()
      );
    } else {
      litData.identifiers = this.getKeyIdentifiers(
        ppssData.shareClasses,
        this.initializeIdentifier()
      );
    }
    this.loadDocumentList(litData, ppssData, listingData);
  }

  // to create multiple row for the fund having same document type
  customLoadShareClassData(
    litData: Map<DctermType, LiteratureDocument[]>,
    ppssData: Product
  ): void {
    this.singleShareClassData = {};
    this.multiShareClassData = {};
    // segregate the data at multishareclass level & singleshareclass level
    this.setDataOnShareClassLevel(litData, ppssData);

    // to create multiple singleShareClass Data when single share class
    // is coming up with multiple combination in multiShareClassData
    if (Object.keys(this.multiShareClassData).length > 1) {
      this.splitMultiShareClassDataToSingleShareClassData();
    }

    // generate rows for listing
    if (
      Object.keys(this.singleShareClassData).length === 0 &&
      Object.keys(this.multiShareClassData).length > 0
    ) {
      // directly pass 'multiShareClassData' data for generating rows
      this.generateCustomRow(this.multiShareClassData, ppssData);
    } else if (
      Object.keys(this.multiShareClassData).length === 0 &&
      Object.keys(this.singleShareClassData).length > 0
    ) {
      // directly pass 'singleShareClassData' data for generating rows
      this.generateCustomRow(this.singleShareClassData, ppssData);
    } else {
      this.mergeMultiShareClassDataToSingleShareClassData(ppssData);
    }
  }

  /**
   *  segregate the data at multishareclass level & singleshareclass level
   * @param litData Map<DctermType, LiteratureDocument[]>
   * @param ppssData Product
   */
  setDataOnShareClassLevel(
    litData: Map<DctermType, LiteratureDocument[]>,
    ppssData: Product
  ): void {
    let filteredShareClassCode: string[] = [];
    let sortedFrkShareClassCode: string;

    const pdsShareClassCode: string[] = [];

    ppssData.shareClasses.forEach((pdsShareClass: ShareClass) => {
      pdsShareClassCode.push(pdsShareClass.shareClassCode);
    });

    litData.forEach((value: LiteratureDocument[], key: DctermType) => {
      value.forEach((document: LiteratureDocument) => {
        if (document?.frkShareClassCode) {
          if (document.frkShareClassCode.length > 1) {
            document.frkShareClassCode = this.updateLiteratureShareClassCode(
              document?.frkShareClassCode
            );
            filteredShareClassCode = [];
            document.frkShareClassCode.forEach((shareClassCode: string) => {
              if (
                pdsShareClassCode.includes(shareClassCode) &&
                !filteredShareClassCode.includes(shareClassCode)
              ) {
                filteredShareClassCode.push(shareClassCode);
              }
            });
            sortedFrkShareClassCode = filteredShareClassCode
              .sort((a: string, b: string) => a.localeCompare(b))
              .toString();
            if (sortedFrkShareClassCode.length) {
              if (sortedFrkShareClassCode in this.multiShareClassData) {
                this.multiShareClassData[sortedFrkShareClassCode] = {
                  ...this.multiShareClassData[sortedFrkShareClassCode],
                  [key]: cloneDeep(document),
                };
              } else {
                this.multiShareClassData[sortedFrkShareClassCode] = {
                  [key]: cloneDeep(document),
                };
              }
            }
          } else {
            if (pdsShareClassCode.includes(document.frkShareClassCode[0])) {
              if (document.frkShareClassCode[0] in this.singleShareClassData) {
                this.singleShareClassData[document.frkShareClassCode[0]] = {
                  ...this.singleShareClassData[document.frkShareClassCode[0]],
                  [key]: cloneDeep(document),
                };
              } else {
                this.singleShareClassData[document.frkShareClassCode[0]] = {
                  [key]: cloneDeep(document),
                };
              }
            }
          }
        }
      });
    });
  }

  /**
   * To create multiple singleShareClass Data when single share class
   * is coming up with multiple combination in multiShareClassData
   */
  splitMultiShareClassDataToSingleShareClassData(): void {
    let shareClassCodes: string[] = [];
    let isDuplicateClassAvailable = false;
    Object.keys(this.multiShareClassData).forEach((key: string) => {
      key.split(',').forEach((classCode) => {
        if (shareClassCodes.indexOf(classCode) === -1) {
          shareClassCodes.push(classCode);
        } else {
          isDuplicateClassAvailable = true;
        }
      });
    });

    if (isDuplicateClassAvailable) {
      shareClassCodes = [];
      Object.keys(this.multiShareClassData).forEach((key) => {
        shareClassCodes = key.split(',');
        shareClassCodes.forEach((classCode) => {
          if (classCode in this.singleShareClassData) {
            this.singleShareClassData[classCode] = {
              ...this.singleShareClassData[classCode],
              ...this.multiShareClassData[key],
            };
          } else {
            this.singleShareClassData[classCode] = {
              ...this.singleShareClassData[classCode],
              ...this.multiShareClassData[key],
            };
          }
        });
      });
      // clearing multiShareClassData
      this.multiShareClassData = {};
    }
  }

  mergeMultiShareClassDataToSingleShareClassData(ppssData: Product): void {
    let flagToAddMultiShareClassRow = true;
    const rowData: object = {};

    Object.keys(this.multiShareClassData).forEach((key) => {
      flagToAddMultiShareClassRow = true;
      let multiShareClassKey: string[] = key.toString().split(',');
      Object.keys(this.singleShareClassData).forEach((key1: string) => {
        if (multiShareClassKey.includes(key1)) {
          rowData[key1] = {
            ...this.singleShareClassData[key1],
            ...this.multiShareClassData[key],
          };
          multiShareClassKey = multiShareClassKey.filter(
            (shareClassCode) => shareClassCode !== key1
          );
          flagToAddMultiShareClassRow = false;
        }
      });

      // To create extra combination when number of single share class data is less than multiShareClass data
      if (multiShareClassKey.length) {
        rowData[multiShareClassKey.toString()] = {
          ...this.multiShareClassData[key],
        };
      }

      // To create a row when there is not exact match for singleShareClass data with multiShareClass data
      if (flagToAddMultiShareClassRow) {
        rowData[key] = { ...this.multiShareClassData[key] };
      }
    });
    this.generateCustomRow(rowData, ppssData);
  }

  /**
   *  add numeric, roman and advisor class to literature share class
   */
  updateLiteratureShareClassCode(frkShareClassCode: string[]): string[] {
    const mappedShareClassCode: string[] = [];
    frkShareClassCode?.forEach((el) => {
      if (el.toLowerCase() === ADVISOR) {
        mappedShareClassCode.push(Z_SHARECLASS);
      } else {
        mappedShareClassCode.push(el);
        const tempClassCode: string = this.mapNumericAndRomanCode(el);
        if (tempClassCode) {
          mappedShareClassCode.push(tempClassCode);
        }
      }
    });
    return mappedShareClassCode;
  }

  mapNumericAndRomanCode(shareClassCode: string): string {
    if (shareClassCode === 'I') {
      return '1';
    }
    if (shareClassCode === 'II') {
      return '2';
    }
    if (shareClassCode === '1') {
      return 'I';
    }
    if (shareClassCode === '2') {
      return 'II';
    }
    if (shareClassCode === 'Primary') {
      return 'P';
    }
    if (shareClassCode === 'Service') {
      return 'S';
    }
    return undefined;
  }

  /**
   * generate row for listing
   */
  generateCustomRow(rowData: object, ppssData: Product) {
    Object.keys(rowData).forEach((key: string) => {
      this.loadCustomDocumentList(
        rowData[key],
        ppssData,
        key.split(','),
        this.setIdentifiers(rowData[key], ppssData),
        this.customListingData
      );
    });
  }

  setIdentifiers(litData, ppssData): KeyIdentifiers {
    let identifiers: KeyIdentifiers = this.initializeIdentifier();
    for (const value in litData) {
      if (litData.hasOwnProperty(value)) {
        this.checkShareClassAvailablity(litData[value], ppssData);
        if (litData[value].shareclass) {
          identifiers = this.getKeyIdentifiers(
            [litData[value].shareclass],
            identifiers
          );
        } else {
          identifiers = this.getKeyIdentifiers(
            ppssData.shareClasses,
            identifiers
          );
        }
      }
    }
    return identifiers;
  }

  initializeIdentifier(): KeyIdentifiers {
    return {
      isin: [],
      bloomberg: [],
      cusip: [],
      reuterSystem: [],
      sedol: [],
      taid: [],
      shareClassFeeStructure: [],
      vln: [],
      wkn: [],
      mexid: [],
      rfi: [],
      apir: [],
      arsn: [],
      adm: [],
      dsc: [],
      ticker: [],
      iress: [],
      iressinavcode: [],
      anbimacode: [],
      intradaynav: [],
      fundbmticker: [],
      distributionIndicator: [],
      dividendFrequency: [],
      shareClassCurrency: [],
    };
  }

  getKeyIdentifiers(shareClasses: ShareClass[], identifier): KeyIdentifiers {
    for (const shareClass of shareClasses) {
      identifier.isin.push(shareClass.identifiers.isin);
      identifier.bloomberg.push(shareClass.identifiers.bloomberg);
      identifier.cusip.push(shareClass.identifiers.cusip);
      identifier.reuterSystem.push(shareClass.identifiers.reuterSystem);
      identifier.sedol.push(shareClass.identifiers.sedol);
      identifier.taid.push(shareClass.identifiers.taid);
      identifier.shareClassFeeStructure.push(shareClass.shareClassFeeStructure);
      identifier.shareClassCurrency.push(shareClass.shareClassCurrency);
      identifier.distributionIndicator.push(shareClass.distributionIndicator);
      identifier.dividendFrequency.push(shareClass.dividendFrequency);
      identifier.wkn.push(shareClass.identifiers.wkn);
      identifier.vln.push(shareClass.identifiers.vln);
      identifier.mexid.push(shareClass.identifiers.mexId);
      identifier.rfi.push(shareClass.identifiers.rfi);
      identifier.apir.push(shareClass.identifiers.apir);
      identifier.arsn.push(shareClass.identifiers.arsn);
      identifier.adm.push(shareClass.identifiers.adm);
      identifier.dsc.push(shareClass.identifiers.dsc);
      identifier.ticker.push(shareClass.identifiers.ticker);
      identifier.iress.push(shareClass.identifiers.iress);
      identifier.iressinavcode.push(shareClass.identifiers.iressinavCode);
      identifier.anbimacode.push(shareClass.identifiers.anbimaCode);
      identifier.intradaynav.push(shareClass.identifiers.intradayNav);
      identifier.fundbmticker.push(shareClass.identifiers.fundbmTicker);
    }
    return identifier;
  }

  isIdentifierValid(key: string): boolean {
    if (key && key.trim() !== '' && key !== '-') {
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.onDestroyUnsubscribe();
  }
}
